import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { buildMetadata } from "@/lib/metadata";
import { ApiError } from "@/lib/api/api-error";
import { cn } from "@/lib/utils";
import { UploadService } from "@/services/upload-service";
import {
  ProductStockUploadService,
  type ProductStockMutationError,
  type ProductStockValidationIssue,
  type StockPreviewRow,
} from "@/services/product-stock-upload-service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ReviewStepper } from "./-components/review-stepper";
import { StockStatusBadge } from "./-components/stock-status";
import {
  loadCatalogProduct,
  StockSkuPicker,
} from "./-components/stock-sku-picker";
import type { Product } from "@/services/types/products";
import { useEffect } from "react";

type StockBrandOption = StockPreviewRow["brand_options"][number];

type DuplicateRowGroup = {
  key: string;
  sku: string;
  brandName: string | null;
  itemName: string;
  incomingTotal: number;
  rowIds: string[];
  count: number;
};

function getMutationErrorPayload(
  error: unknown,
): ProductStockMutationError | null {
  if (!(error instanceof ApiError) || !error.body || typeof error.body !== "object") {
    return null;
  }

  const body = error.body as Record<string, unknown>;
  const errors = Array.isArray(body.errors)
    ? (body.errors as ProductStockValidationIssue[])
    : undefined;

  return {
    detail: typeof body.detail === "string" ? body.detail : undefined,
    errors,
  };
}

function buildBrandOptionsFromProduct(
  product?: Product | null,
): StockBrandOption[] {
  return (product?.product_brands ?? [])
    .filter((productBrand) => productBrand.is_active)
    .map((productBrand) => ({
      product_brand_id: productBrand.id,
      brand_id: productBrand.brand_id,
      brand_name: productBrand.brand_name,
      is_default: productBrand.is_default,
    }));
}

function getDefaultBrandId(
  brandOptions: StockBrandOption[],
): number | null {
  const defaultBrand = brandOptions.find((option) => option.is_default);
  if (defaultBrand) {
    return defaultBrand.product_brand_id;
  }
  if (brandOptions.length === 1) {
    return brandOptions[0].product_brand_id;
  }
  return null;
}

function getSelectedBrandName(row: StockPreviewRow): string | null {
  if (!row.selected_product_brand_id) {
    return null;
  }
  return (
    row.brand_options.find(
      (option) => option.product_brand_id === row.selected_product_brand_id,
    )?.brand_name ?? null
  );
}

function getStockStatus(
  oldStock: number,
  newStock: number,
): StockPreviewRow["status"] {
  if (oldStock <= 0 && newStock > 0) {
    return "restocked";
  }
  if (newStock <= 5) {
    return "low";
  }
  return "ok";
}

function parseIncomingValue(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number(trimmed);
  if (Number.isNaN(parsed) || parsed < 0) {
    return null;
  }
  return parsed;
}

function withIncomingUpdate(
  row: StockPreviewRow,
  incoming: number,
): StockPreviewRow {
  const newStock = row.old_stock + incoming;
  return {
    ...row,
    incoming,
    incoming_display: `+${incoming}`,
    new_stock: newStock,
    status: getStockStatus(row.old_stock, newStock),
  };
}

const ADDED_ROW_PREFIX = "added-";

function isAddedRow(row: Pick<StockPreviewRow, "id"> | string): boolean {
  const id = typeof row === "string" ? row : row.id;
  return id.startsWith(ADDED_ROW_PREFIX);
}

function createAddedRow(): StockPreviewRow {
  return {
    id: `${ADDED_ROW_PREFIX}${crypto.randomUUID()}`,
    sku_name: "",
    sku_id: "",
    sku_id_options: [],
    sku_matches: [],
    file: "manual",
    old_stock: 0,
    incoming: 0,
    incoming_display: "+0",
    new_stock: 0,
    uom: "",
    status: "low",
    original_name: "",
    brand_required: false,
    selected_product_brand_id: null,
    brand_options: [],
  };
}

function getProductOldStock(
  product: Product | undefined,
  warehouseId: number | null,
  productBrandId: number | null,
): number {
  if (!product) {
    return 0;
  }
  if (productBrandId) {
    const brandStock = product.brand_warehouse_stocks.find(
      (stock) =>
        stock.product_brand_id === productBrandId &&
        (warehouseId == null || stock.warehouse_id === warehouseId),
    );
    if (brandStock?.quantity_available != null) {
      return Number(brandStock.quantity_available);
    }
    return 0;
  }
  if (warehouseId != null) {
    const warehouseStock = product.warehouse_stocks.find(
      (stock) => stock.warehouse_id === warehouseId,
    );
    if (warehouseStock?.quantity_available != null) {
      return Number(warehouseStock.quantity_available);
    }
  }
  return Number(product.quantity_available ?? 0);
}

function withSkuSelection(
  row: StockPreviewRow,
  product: Product | undefined,
  sku: string,
  name: string,
  warehouseId: number | null,
): StockPreviewRow {
  const brandOptions = buildBrandOptionsFromProduct(product);
  const selectedProductBrandId = getDefaultBrandId(brandOptions);
  const oldStock = getProductOldStock(
    product,
    warehouseId,
    selectedProductBrandId,
  );
  const newStock = oldStock + row.incoming;
  return {
    ...row,
    sku_id: sku,
    sku_name: name,
    original_name: name,
    uom: product?.uom ?? row.uom,
    brand_required: brandOptions.length > 0,
    brand_options: brandOptions,
    selected_product_brand_id: selectedProductBrandId,
    old_stock: oldStock,
    new_stock: newStock,
    status: getStockStatus(oldStock, newStock),
  };
}

function buildDuplicateGroups(
  rows: StockPreviewRow[],
  removedIds: Set<string>,
): DuplicateRowGroup[] {
  const groups = new Map<string, DuplicateRowGroup>();

  for (const row of rows) {
    if (removedIds.has(row.id) || !row.sku_id) {
      continue;
    }
    if (row.brand_required && !row.selected_product_brand_id) {
      continue;
    }

    const brandName = getSelectedBrandName(row);
    const key = `${row.sku_id}::${row.selected_product_brand_id ?? "aggregate"}`;
    const existing = groups.get(key);
    if (existing) {
      existing.incomingTotal += row.incoming;
      existing.count += 1;
      existing.rowIds.push(row.id);
      continue;
    }

    groups.set(key, {
      key,
      sku: row.sku_id,
      brandName,
      itemName: row.original_name || row.sku_name,
      incomingTotal: row.incoming,
      rowIds: [row.id],
      count: 1,
    });
  }

  return Array.from(groups.values()).filter((group) => group.count > 1);
}

export const Route = createFileRoute(
  "/_main/products/upload/review/$sessionId",
)({
  component: RouteComponent,
  head: () => ({
    meta: buildMetadata({
      title: "Review stock update • Products",
      description: "Match SKUs and confirm stock changes.",
      keywords: ["products", "stock", "review"],
    }),
  }),
});

function RouteComponent() {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const uploadRequestQuery = useQuery({
    queryKey: ["product-stock-upload-request", sessionId],
    queryFn: () => UploadService.getUploadRequest(sessionId),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) {
        return false;
      }
      return data.status === "pending" || data.status === "processing"
        ? 3000
        : false;
    },
  });
  const isProcessing =
    uploadRequestQuery.data?.status === "pending" ||
    uploadRequestQuery.data?.status === "processing";

  const [productsBySku, setProductsBySku] = useState<Record<string, Product>>(
    {},
  );

  const reviewQuery = useQuery({
    queryKey: ["product-stock-upload-review", sessionId],
    queryFn: () => ProductStockUploadService.getReview(sessionId),
    enabled: uploadRequestQuery.data?.status === "completed",
  });

  const [removedUnrecognized, setRemovedUnrecognized] = useState<Set<string>>(
    () => new Set(),
  );
  const [stockItems, setStockItems] = useState<StockPreviewRow[]>([]);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [incomingDraft, setIncomingDraft] = useState("");
  const [incomingSnapshot, setIncomingSnapshot] = useState<number | null>(null);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(
    null,
  );
  const warehouseChangeVersion = useRef(0);

  const unrecognized = useMemo(() => {
    const items = reviewQuery.data?.unrecognized_items ?? [];
    return items.filter((u) => !removedUnrecognized.has(u.id));
  }, [reviewQuery.data?.unrecognized_items, removedUnrecognized]);
  const duplicateGroups = useMemo(
    () => buildDuplicateGroups(stockItems, removedUnrecognized),
    [stockItems, removedUnrecognized],
  );
  const duplicateRowIds = useMemo(
    () => new Set(duplicateGroups.flatMap((group) => group.rowIds)),
    [duplicateGroups],
  );
  const validationIssues = reviewQuery.data?.validation_issues ?? [];
  const hasBlockingIssues = validationIssues.length > 0;

  const hasMissingBrandSelection = stockItems.some(
    (row) =>
      row.brand_required &&
      !removedUnrecognized.has(row.id) &&
      !row.selected_product_brand_id,
  );
  const hasIncompleteAddedRows = stockItems.some(
    (row) => isAddedRow(row) && !row.sku_id,
  );
  const hasInvalidAddedQuantity = stockItems.some(
    (row) => isAddedRow(row) && row.incoming <= 0,
  );

  useEffect(() => {
    const serverRows = reviewQuery.data?.stock_items ?? [];
    setStockItems((prev) => {
      const addedRows = prev.filter((row) => isAddedRow(row));
      return [...serverRows, ...addedRows];
    });
  }, [reviewQuery.data?.stock_items]);

  useEffect(() => {
    const selectedWarehouse = reviewQuery.data?.selected_warehouse_id;
    if (selectedWarehouse) {
      setSelectedWarehouseId(selectedWarehouse);
      return;
    }
    const warehouses = reviewQuery.data?.available_warehouses ?? [];
    if (warehouses.length === 0) {
      return;
    }
    const defaultWarehouse = warehouses.find((warehouse) => warehouse.is_default);
    setSelectedWarehouseId((defaultWarehouse ?? warehouses[0]).id);
  }, [
    reviewQuery.data?.selected_warehouse_id,
    reviewQuery.data?.available_warehouses,
  ]);

  const applyIncomingToRow = (rowId: string, incoming: number) => {
    setStockItems((prev) =>
      prev.map((item) =>
        item.id === rowId ? withIncomingUpdate(item, incoming) : item,
      ),
    );
  };

  const commitIncomingEdit = (row: StockPreviewRow) => {
    const parsed = parseIncomingValue(incomingDraft);
    if (parsed !== null) {
      applyIncomingToRow(row.id, parsed);
    } else if (incomingSnapshot !== null) {
      applyIncomingToRow(row.id, incomingSnapshot);
    }
    setEditingRowId(null);
    setIncomingDraft("");
    setIncomingSnapshot(null);
  };

  const cancelIncomingEdit = (row: StockPreviewRow) => {
    if (incomingSnapshot !== null) {
      applyIncomingToRow(row.id, incomingSnapshot);
    }
    setEditingRowId(null);
    setIncomingDraft("");
    setIncomingSnapshot(null);
  };

  const startIncomingEdit = (row: StockPreviewRow) => {
    if (editingRowId === row.id) {
      commitIncomingEdit(row);
      return;
    }
    if (editingRowId) {
      const currentRow = stockItems.find((item) => item.id === editingRowId);
      if (currentRow) {
        const parsed = parseIncomingValue(incomingDraft);
        if (parsed !== null) {
          applyIncomingToRow(currentRow.id, parsed);
        } else if (incomingSnapshot !== null) {
          applyIncomingToRow(currentRow.id, incomingSnapshot);
        }
      }
    }
    setEditingRowId(row.id);
    setIncomingDraft(String(row.incoming));
    setIncomingSnapshot(row.incoming);
  };

  const handleAction = async () => {
    try {
      await confirmMutation.mutateAsync();
      await applyMutation.mutateAsync();
      await router.invalidate();
      navigate({ to: "/products" });
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const applyMutation = useMutation({
    mutationFn: () => ProductStockUploadService.apply(sessionId),
    onSuccess: async (data) => {
      toast.success(`Updated ${data.updated_count} product stock levels.`);
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({
        queryKey: ["stock-upload-sku-picker"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["product-stock-upload-preview", sessionId],
      });
    },
    onError: (error) => {
      const payload = getMutationErrorPayload(error);
      if (payload?.errors?.length) {
        toast.error(payload.detail ?? "Failed to apply stock updates.", {
          description: payload.errors
            .slice(0, 2)
            .map((issue) => `Line ${issue.line_number}: ${issue.message}`)
            .join(" "),
        });
        return;
      }
      if (payload?.detail) {
        toast.error(payload.detail);
        return;
      }
      toast.error("Failed to apply stock updates.");
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () => {
      const persistedRows = stockItems.filter((row) => !isAddedRow(row));
      const skuOverrides = persistedRows.reduce<Record<string, string>>(
        (acc, row) => {
          if (row.sku_id) {
            acc[row.id] = row.sku_id;
          }
          return acc;
        },
        {},
      );
      const brandOverrides = persistedRows.reduce<Record<string, number>>(
        (acc, row) => {
          if (row.brand_required && row.selected_product_brand_id) {
            acc[row.id] = row.selected_product_brand_id;
          }
          return acc;
        },
        {},
      );
      const incomingOverrides = persistedRows.reduce<Record<string, number>>(
        (acc, row) => {
          acc[row.id] = row.incoming;
          return acc;
        },
        {},
      );
      const addedItems = stockItems
        .filter((row) => isAddedRow(row) && row.sku_id)
        .map((row) => ({
          client_id: row.id.slice(ADDED_ROW_PREFIX.length),
          sku: row.sku_id,
          incoming: row.incoming,
          product_brand_id: row.selected_product_brand_id ?? undefined,
        }));
      return ProductStockUploadService.confirm(sessionId, {
        warehouse_id: selectedWarehouseId ?? undefined,
        sku_overrides: skuOverrides,
        brand_overrides: brandOverrides,
        incoming_overrides: incomingOverrides,
        added_items: addedItems,
        removed_unrecognized_ids: Array.from(removedUnrecognized),
      });
    },
    onSuccess: async () => {
      setStockItems((prev) => prev.filter((row) => !isAddedRow(row)));
      await queryClient.invalidateQueries({
        queryKey: ["product-stock-upload-review", sessionId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["product-stock-upload-preview", sessionId],
      });
    },
    onError: (error) => {
      const payload = getMutationErrorPayload(error);
      if (payload?.errors?.length) {
        toast.error(payload.detail ?? "Could not confirm. Try again.", {
          description: payload.errors
            .slice(0, 2)
            .map((issue) => `Line ${issue.line_number}: ${issue.message}`)
            .join(" "),
        });
        return;
      }
      if (payload?.detail) {
        toast.error(payload.detail);
        return;
      }
      toast.error("Could not confirm. Try again.");
    },
  });

  const cacheProduct = (product: Product | undefined) => {
    if (!product?.sku) {
      return;
    }
    setProductsBySku((prev) =>
      prev[product.sku] === product ? prev : { ...prev, [product.sku]: product },
    );
  };

  const handleWarehouseChange = async (warehouseId: number) => {
    const version = ++warehouseChangeVersion.current;
    const previousWarehouseId = selectedWarehouseId;
    setSelectedWarehouseId(warehouseId);

    const resolvedProducts = new Map<string, Product>();
    let lookupFailed = false;
    await Promise.all(
      stockItems.map(async (row) => {
        if (!row.sku_id) {
          return;
        }
        let product: Product | undefined = productsBySku[row.sku_id];
        if (!product) {
          const matched = row.sku_matches.find(
            (candidate) => candidate.sku === row.sku_id,
          );
          try {
            product = await loadCatalogProduct({
              sku: row.sku_id,
              productId: matched?.product_id,
            });
          } catch {
            product = undefined;
          }
        }
        if (product) {
          resolvedProducts.set(row.sku_id, product);
        } else {
          lookupFailed = true;
        }
      }),
    );

    if (version !== warehouseChangeVersion.current) {
      return;
    }
    if (lookupFailed) {
      setSelectedWarehouseId(previousWarehouseId);
      toast.error(
        "Could not recalculate stock for that warehouse. Please try again.",
      );
      return;
    }
    setProductsBySku((prev) => ({
      ...prev,
      ...Object.fromEntries(resolvedProducts),
    }));
    setStockItems((currentRows) =>
      currentRows.map((row) => {
        const product = resolvedProducts.get(row.sku_id);
        if (!product) {
          return row;
        }
        const oldStock = getProductOldStock(
          product,
          warehouseId,
          row.selected_product_brand_id ?? null,
        );
        const newStock = oldStock + row.incoming;
        return {
          ...row,
          old_stock: oldStock,
          new_stock: newStock,
          status: getStockStatus(oldStock, newStock),
        };
      }),
    );
  };

  if (uploadRequestQuery.isPending || isProcessing) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center p-8">
        <div className="space-y-3 text-center">
          <Loader2 className="mx-auto size-10 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground text-sm">
            Processing your stock document...
          </p>
        </div>
      </div>
    );
  }

  if (
    uploadRequestQuery.isError ||
    uploadRequestQuery.data?.status === "failed" ||
    reviewQuery.isError ||
    !reviewQuery.data
  ) {
    return (
      <div className="space-y-4 p-8">
        <p className="text-destructive">
          {uploadRequestQuery.data?.error_message || "Could not load review data."}
        </p>
        <Button asChild variant="outline">
          <Link to="/products/upload">Back to upload</Link>
        </Button>
      </div>
    );
  }

  const data = reviewQuery.data;
  const lineLabel = `${data.line_items} line items`;
  const warehouseSelectionRequired = data.available_warehouses.length > 1;

  return (
    <div className="space-y-8 p-4 pb-28 md:p-8">
      <div className="space-y-6">
        <Link
          to="/products/upload"
          className="inline-flex text-muted-foreground text-sm hover:text-foreground"
        >
          ← Upload a different file
        </Link>
        <div className="space-y-2">
          <h1 className="font-semibold text-2xl tracking-tight">
            Review &amp; Confirm Stock Update
          </h1>
          <p className="text-muted-foreground text-sm">
            Match SKUs and confirm changes · {data.filename} · {lineLabel}
          </p>
        </div>
        <ReviewStepper activeIndex={1} />
      </div>

      {unrecognized.length > 0 ? (
        <section
          className="rounded-xl border border-amber-200/80 bg-amber-50/80 p-4 md:p-6"
          aria-label="Unrecognized items"
        >
          <h2 className="font-semibold text-amber-950">
            Unrecognized Items ({unrecognized.length})
          </h2>
          <p className="mt-1 text-amber-900/80 text-sm">
            These items couldn&apos;t be matched. Resolve or remove before
            confirming.
          </p>
          <ul className="mt-4 space-y-3">
            {unrecognized.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-background px-4 py-3"
              >
                <span className="font-medium">{item.name}</span>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    hidden={true}
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    onClick={() =>
                      toast.message("Resolve", {
                        description:
                          "Mapping UI can be wired when ERP rules are ready.",
                      })
                    }
                  >
                    Resolve
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    aria-label={`Remove ${item.name}`}
                    onClick={() =>
                      setRemovedUnrecognized((prev) =>
                        new Set(prev).add(item.id),
                      )
                    }
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {duplicateGroups.length > 0 ? (
        <section
          className="rounded-xl border border-red-200/80 bg-red-50/80 p-4 md:p-6"
          aria-label="Duplicate items"
        >
          <h2 className="font-semibold text-red-950">
            Duplicate Product Rows ({duplicateGroups.length})
          </h2>
          <p className="mt-1 text-red-900/80 text-sm">
            Duplicate rows would silently sum stock. Consolidate or remove them
            before confirming this upload.
          </p>
          <ul className="mt-4 space-y-3">
            {duplicateGroups.map((group) => (
              <li
                key={group.key}
                className="rounded-lg border bg-background px-4 py-3 text-sm"
              >
                <span className="font-medium">
                  {group.itemName} ({group.sku})
                </span>
                <span className="text-muted-foreground">
                  {" "}
                  appears {group.count} times
                  {group.brandName ? ` for brand ${group.brandName}` : ""} with
                  incoming total +{group.incomingTotal}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {validationIssues.length > 0 ? (
        <section
          className="rounded-xl border border-red-200/80 bg-red-50/80 p-4 md:p-6"
          aria-label="Blocking issues"
        >
          <h2 className="font-semibold text-red-950">
            Blocking Issues ({validationIssues.length})
          </h2>
          <p className="mt-1 text-red-900/80 text-sm">
            Fix these rows before confirming this upload.
          </p>
          <ul className="mt-4 space-y-3">
            {validationIssues.map((issue) => (
              <li
                key={`${issue.id}:${issue.code}`}
                className="rounded-lg border bg-background px-4 py-3 text-sm"
              >
                <span className="font-medium">
                  Line {issue.line_number}
                  {issue.sku ? ` · ${issue.sku}` : ""}
                </span>
                <span className="ml-2 text-muted-foreground">
                  {issue.message}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="overflow-hidden rounded-xl border bg-card">
        <div className="flex flex-col gap-4 border-b px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold text-lg">Stock Items</h2>
            <Badge className="rounded-md border-0 bg-brand-yellowGreen font-medium text-brand-darkGreen hover:bg-brand-yellowGreen">
              {data.stats.total_items} items
            </Badge>
            <Badge
              variant="outline"
              className="rounded-md border-red-200 bg-red-50 font-medium text-red-700"
            >
              {data.stats.low_stock_count} low stock
            </Badge>
          </div>
          <div className="flex flex-col gap-3 md:items-end">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">Warehouse</span>
              <Select
                value={selectedWarehouseId ? String(selectedWarehouseId) : undefined}
                onValueChange={(value) => void handleWarehouseChange(Number(value))}
                disabled={data.available_warehouses.length === 0}
              >
                <SelectTrigger className="h-9 w-64 rounded-md">
                  {selectedWarehouseId
                    ? data.available_warehouses.find(
                        (warehouse) => warehouse.id === selectedWarehouseId,
                      )?.name
                    : data.available_warehouses.length > 0
                      ? "Select warehouse"
                      : "No warehouse available"}
                </SelectTrigger>
                <SelectContent className="w-72">
                  {data.available_warehouses.map((warehouse) => (
                    <SelectItem key={warehouse.id} value={String(warehouse.id)}>
                      {warehouse.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-fit rounded-lg"
              onClick={() =>
                setStockItems((prev) => [...prev, createAddedRow()])
              }
            >
              <Plus className="mr-1 size-4" />
              Add item
            </Button>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="font-semibold">SKU Name</TableHead>
              <TableHead className="font-semibold">SKU ID</TableHead>
              <TableHead className="font-semibold">Brand</TableHead>
              <TableHead className="text-center font-semibold">
                Old Stock
              </TableHead>
              <TableHead className="text-center font-semibold">
                Incoming
              </TableHead>
              <TableHead className="text-center font-semibold">
                New Stock
              </TableHead>
              <TableHead className="font-semibold">UOM</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="w-[72px] text-right font-semibold">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {stockItems.map((thisRow) => {
              return <TableRow
                key={thisRow.id}
                className={cn(
                  duplicateRowIds.has(thisRow.id) ? "bg-red-50/60" : undefined,
                )}
              >
                <TableCell className="font-medium">
                  {thisRow.original_name ||
                    thisRow.sku_name ||
                    (isAddedRow(thisRow) ? "New item" : "")}
                </TableCell>
                <TableCell>
                  <StockSkuPicker
                    row={thisRow}
                    onSelect={({ sku, name, product }) => {
                      if (sku === thisRow.sku_id && product == null) {
                        return;
                      }
                      cacheProduct(product);
                      setStockItems((prev) =>
                        prev.map((item) =>
                          item.id === thisRow.id
                            ? withSkuSelection(
                                item,
                                product,
                                sku,
                                name || item.sku_name,
                                selectedWarehouseId,
                              )
                            : item,
                        ),
                      );
                    }}
                  />
                </TableCell>
                <TableCell>
                  {thisRow.brand_required ? (
                    <Select
                      value={
                        thisRow.selected_product_brand_id
                          ? String(thisRow.selected_product_brand_id)
                          : undefined
                      }
                      onValueChange={(value) => {
                        const productBrandId = Number(value);
                        const applyBrandStock = (product: Product | undefined) => {
                          const oldStock = getProductOldStock(
                            product,
                            selectedWarehouseId,
                            productBrandId,
                          );
                          setStockItems((prev) =>
                            prev.map((item) => {
                              if (item.id !== thisRow.id) {
                                return item;
                              }
                              const newStock = oldStock + item.incoming;
                              return {
                                ...item,
                                selected_product_brand_id: productBrandId,
                                old_stock: oldStock,
                                new_stock: newStock,
                                status: getStockStatus(oldStock, newStock),
                              };
                            }),
                          );
                        };

                        const cached = productsBySku[thisRow.sku_id];
                        if (cached) {
                          applyBrandStock(cached);
                          return;
                        }

                        const matched = thisRow.sku_matches.find(
                          (candidate) => candidate.sku === thisRow.sku_id,
                        );
                        void loadCatalogProduct({
                          sku: thisRow.sku_id,
                          productId: matched?.product_id,
                        }).then((product) => {
                          cacheProduct(product);
                          applyBrandStock(product);
                        });
                      }}
                    >
                      <SelectTrigger className="h-9 w-[min(180px,100%)] rounded-md">
                        {thisRow.selected_product_brand_id
                          ? thisRow.brand_options.find(
                              (option) =>
                                option.product_brand_id ===
                                thisRow.selected_product_brand_id,
                            )?.brand_name
                          : "Select brand"}
                      </SelectTrigger>
                      <SelectContent className="w-64">
                        {thisRow.brand_options.map((option) => (
                          <SelectItem
                            key={option.product_brand_id}
                            value={String(option.product_brand_id)}
                          >
                            <div className="flex w-full min-w-0 items-center justify-between gap-2">
                              <span className="truncate font-medium text-xs">
                                {option.brand_name}
                              </span>
                              {option.is_default ? (
                                <Badge
                                  variant="secondary"
                                  className="rounded-full px-2 py-0 text-[10px]"
                                >
                                  Default
                                </Badge>
                              ) : null}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline" className="font-normal text-xs">
                      Warehouse only
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  <span
                    className={cn(
                      "inline-flex min-w-[3rem] justify-center rounded-md border bg-muted/40 px-2 py-1 tabular-nums",
                    )}
                  >
                    {thisRow.old_stock}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  {editingRowId === thisRow.id ? (
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      autoFocus
                      value={incomingDraft}
                      aria-label={`Incoming quantity for ${thisRow.sku_name}`}
                      className="mx-auto h-8 min-w-[3rem] text-center tabular-nums"
                      onChange={(event) => {
                        const value = event.target.value;
                        setIncomingDraft(value);
                        const parsed = parseIncomingValue(value);
                        if (parsed !== null) {
                          applyIncomingToRow(thisRow.id, parsed);
                        }
                      }}
                      onBlur={() => commitIncomingEdit(thisRow)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.currentTarget.blur();
                        }
                        if (event.key === "Escape") {
                          event.preventDefault();
                          cancelIncomingEdit(thisRow);
                        }
                      }}
                    />
                  ) : (
                    <span className="inline-flex w-full justify-center rounded-md border bg-muted/40 px-2 py-1 tabular-nums">
                      {thisRow.incoming}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  <span className="inline-flex w-full justify-center rounded-md border bg-muted/40 px-2 py-1 tabular-nums">
                    {thisRow.new_stock}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="rounded-md border bg-muted/50 px-2 py-1 text-xs">
                    {thisRow.uom}
                  </span>
                </TableCell>
                <TableCell>
                  <StockStatusBadge status={thisRow.status} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground"
                      aria-label={`Edit ${thisRow.sku_name || "incoming quantity"}`}
                      aria-pressed={editingRowId === thisRow.id}
                      onMouseDown={(event) => {
                        if (editingRowId === thisRow.id) {
                          event.preventDefault();
                        }
                      }}
                      onClick={() => startIncomingEdit(thisRow)}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    {isAddedRow(thisRow) ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        aria-label={`Remove ${thisRow.sku_name || "added item"}`}
                        onClick={() =>
                          setStockItems((prev) =>
                            prev.filter((item) => item.id !== thisRow.id),
                          )
                        }
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            })}
          </TableBody>
        </Table>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-10 flex justify-end gap-3 border-t bg-background/95 px-4 py-4 backdrop-blur md:px-8">
        {hasBlockingIssues ? (
          <p className="mr-auto text-red-700 text-sm">
            Resolve blocking validation issues before syncing stock.
          </p>
        ) : hasIncompleteAddedRows ? (
          <p className="mr-auto text-muted-foreground text-sm">
            Select a SKU for each added item before syncing stock.
          </p>
        ) : hasInvalidAddedQuantity ? (
          <p className="mr-auto text-muted-foreground text-sm">
            Added items must have an incoming quantity greater than zero.
          </p>
        ) : null}
        <Button variant="outline" className="rounded-lg" asChild>
          <Link to="/products">Discard &amp; Cancel</Link>
        </Button>
        <Button
          className="rounded-lg bg-foreground text-background hover:bg-foreground/90"
          disabled={
            confirmMutation.isPending ||
            applyMutation.isPending ||
            (warehouseSelectionRequired && !selectedWarehouseId) ||
            hasMissingBrandSelection ||
            hasIncompleteAddedRows ||
            hasInvalidAddedQuantity ||
            duplicateGroups.length > 0 ||
            hasBlockingIssues
          }
          onClick={() => handleAction()}
        >
          {confirmMutation.isPending || applyMutation.isPending ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Confirming…
            </>
          ) : (
            "Confirm & Sync"
          )}
        </Button>
      </div>
    </div>
  );
}
